import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import React from 'react';
// @mui
import {
  DataGridPro,
  GridColDef,
  GridToolbar,
  GridColumnVisibilityModel,
  GridLogicOperator,
  useGridApiRef,
  GridSortModel,
  GridFilterModel,
  GridPaginationModel,
  GridColType,
  GridAlignment,
} from '@mui/x-data-grid-pro';
import { Box, Stack, useTheme, useMediaQuery, width, minWidth, maxWidth } from '@mui/system';
import { LinearProgress, Typography, Tooltip, IconButton, Button, Menu, MenuItem, ListItemIcon, ListItemText, Divider, TextField, InputAdornment, Chip, CircularProgress, Grid, Theme } from '@mui/material';
import { fNumber, fPercent } from 'src/utils/format-number';
import { setFilterKeyword } from 'src/context/reducers/filter-settings/filterSlice';
import { useDispatch, useSelector } from 'react-redux';
import { useLocales } from 'src/locales';
import { useResponsive } from 'src/hooks/use-responsive';
import { useCurrentWorkspace } from 'src/context/reducers/app-settings';
import { formatDate } from 'src/utils/dates-labels';
import { formatCurrency } from 'src/utils/format-currency';
import CustomLineChart from '../inline-chart/inline-chart';
import FilterPanelCustom from './filter-panel-custom/filter-panel-custom';
import { useLocation } from 'react-router-dom';
import ReactDOM from 'react-dom';
import CompareChip from 'src/components/chip/compare-chip';
import DatagridDiffCell from './data-grid-diff-cell';

import "./data-grid-custom.css";
import { i18n } from 'i18next';
import { RootState } from 'src/context/store';
import { get, merge } from 'lodash';
import { useKpisQuery } from 'src/context/api/data/api';
import { fDate } from 'src/utils/format-time';
import Iconify from '../iconify';

// ----------------------------------------------------------------------


export enum ColumnType {
  COMPARISON = 'comparison',
  DIFF = 'diff',
  LINE = 'line',
}

type LineCellProps = {
  data: any;
  kpi: ReturnType<typeof useKpisQuery>["data"][number];
  total: number;
  type: "bar" | "line";
}

const valueOrZero = (value: any) => value ?? 0;
const valueOrDate = (value: any) => value ?? fDate(new Date(), "yyyy-MM-dd");
const valueOrEmpty = (value: any) => value ?? "";

const useGetFormatter = (kpi: ReturnType<typeof useKpisQuery>["data"][number]) => {
  const { currentLang: { value: locale } } = useLocales();
  const workspace = useCurrentWorkspace();
  const [formatter, nbDecimals] = kpi?.format?.split(":") ?? ["", 0];
  const nbDecimalNumber = parseInt(nbDecimals);
  const formatterFunction = ["date", "date_day"].includes(kpi.id) ? (value: any = "") => {
    return fDate(valueOrDate(value), "yyyy-MM-dd")
  } :
    ["device", "device_type"].includes(kpi.id) ? (value: any = "") => {
      value = valueOrEmpty(value);
      return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase()
    } :
      formatter === "currency" ? (value: number = 0) => {
        return Intl.NumberFormat(locale, {
          style: "currency",
          currency: workspace?.currency,
          minimumFractionDigits: nbDecimalNumber,
          maximumFractionDigits: nbDecimalNumber,
          notation: value > 1000 ? "compact" : "standard",
        }).format(valueOrZero(value));
      } : formatter === "percentage" ? (value: number = 0) => ((valueOrZero(value))?.toFixed(nbDecimalNumber) ?? 0) + "%" : ["number", "float"].includes(formatter) ? (value: number = 0) => {
        return valueOrZero(value).toLocaleString(locale, { maximumFractionDigits: nbDecimalNumber })
      } : (value: any) => value;
  return formatterFunction;
}

const LineCell = ({ data, kpi, total, type }: LineCellProps) => {
  const { currentLang: { value: locale } } = useLocales();
  const formatterFunction = useGetFormatter(kpi);
  const series = data.map((item: any) => ({ x: item.date, y: item.value ?? 0 }));

  return (
    <Stack spacing={1} direction="row" alignItems="center" sx={{ px: 1, width: 1, height: 1, overflow: 'visible' }}>
      <CustomLineChart formatValue={formatterFunction} series={[{
        name: kpi[locale as keyof typeof kpi] as string,
        data: series
      }]} type={type} color={kpi.color} height={30} width={100} />
      <Typography variant="caption" sx={{ width: 80, fontSize: "0.825rem" }}>
        {formatterFunction(total)}
      </Typography>
    </Stack>
  )
}

const ProgressCell = ({ row, kpi }: { row: any, kpi: any }) => {
  const theme = useTheme();
  const cappedValue = Math.min(row[kpi.id], 100); // Limiter à 100%
  return (
    <Stack spacing={1} direction="row" alignItems="center" sx={{ px: 1, width: 1, height: 1 }}>
      <LinearProgress
        value={cappedValue}
        variant="determinate"
        sx={{
          width: 1,
          height: 6,
          backgroundColor: theme.palette.mode === "dark" ? kpi["color:dark"] : kpi["color:light"],
          '& .MuiLinearProgress-bar': {
            backgroundColor: kpi["color"],
          }
        }}
      />
      <Typography variant="caption" sx={{ width: 80, fontSize: "0.825rem" }}>
        {cappedValue === 0 ? '0%' : fPercent(cappedValue)}
      </Typography>
    </Stack>
  )
}

type DiffCellProps = {
  kpi: ReturnType<typeof useKpisQuery>["data"][number];
  value: number;
  compare: number;
}

const DiffCell = ({ value, compare, kpi }: DiffCellProps) => {
  const [format, nbDecimals] = kpi.format.split(":") ?? ["", 0];
  const formatterFunction = useGetFormatter(kpi);

  const diff = (value - compare);
  const diffPercentage = (value - compare) / compare * 100;

  return <DatagridDiffCell
    value={parseFloat(diff.toFixed(nbDecimals))}
    compareChip={diffPercentage}
    invertedColor={kpi.invert}
    unit={format === "percentage" ? "pp" : "currency"}
  />

}

const BaseCell = ({ row, kpi }: { row: any, kpi: any }) => {
  const formatterFunction = useGetFormatter(kpi);
  return <Typography variant="caption" sx={{ width: 80, fontSize: kpi.viz === "dimension" ? "0.85rem" : "0.825rem" }}>
    {formatterFunction(row[kpi.id])}
  </Typography>
}

const useMakeCell = () => {
  const { currentLang: { value: locale } } = useLocales();
  const filterStore = useSelector((state: RootState) => state.filters);
  const lgUp = useResponsive('up', 'lg');
  const kpis = useKpisQuery().data ?? [];

  return (row: any, kpi: any, key: string) => {
    const sparkline =
      "sparkline" in row &&
      Object.keys(row.sparkline ?? {})
        .map((item: string) => item.split(":")[1])
        .includes(key);

    const compareEnabled = filterStore?.compare;
    const columns: (GridColDef & { hide?: boolean })[] = [];

    const [type, align]: [GridColType, GridAlignment] =
      kpi.viz === "dimension" ? ["string", "left"] : ["number", "right"];

    const Progress =
      kpi.format.split(":").includes("percentage") &&
      ["date", "date_day", "year_week", "year_month"].some((item) =>
        Object.keys(row).includes(item)
      )
        ? ProgressCell
        : BaseCell;

    const cell = (row: any) => {
      if (
        sparkline &&
        !["date", "date_day", "year_week", "year_month"].some((item) =>
          Object.keys(row).includes(item)
        )
      ) {
        const [type, dataKey, data = []] = Object.entries(row.sparkline ?? {})
          .map(([item, data]: ["bar" | "line" | string, any]) =>
            item.split(":").concat([data])
          )
          .find(([, value]) => value === key) ?? [];
        return <LineCell data={data} kpi={kpi} total={row[key]} type={type as "bar" | "line"} />;
      }
      return <Progress row={row} kpi={kpi} />;
    };

    const HeaderCell = (suffix: string = "", compare: boolean = false) => (
      <div style={{ whiteSpace: "pre-line", display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
        <span style={{ textAlign: "right" }}>
          {kpi[locale as keyof typeof kpis[0]]} {suffix}
        </span>
        {compareEnabled && suffix === "Compare" && (
          <span style={{ textAlign: "right" }}>
            {formatDate(compare ? filterStore?.compareStart : filterStore?.start, locale)} -{" "}
            {formatDate(compare ? filterStore?.compareEnd : filterStore?.end, locale)}
          </span>
        )}
      </div>
    );

    const [minWidth, maxWidth] = sparkline ? [225, 500] : [190, 500] as [number, number];

    const valueGetter =
      ["date", "date_day"].includes(key)
        ? (_: any, row: any) => formatDate(row[key], locale)
        : (_: any, row: any) => row[key];

    // ✅ Colonne principale
    columns.push({
      field: key,
      hide: kpi.viz !== "dimension" ? !kpi.show : false,
      cellClassName: sparkline ? "sparkline-cell" : undefined,
      align,
      type,
      headerName: kpi[locale as keyof typeof kpis[0]],
      minWidth,
      maxWidth,
      flex: lgUp ? 1 : undefined,
      renderHeader: () => HeaderCell(),
      renderCell: (params: any) => cell(params.row),
      valueGetter,
    });

    const isDate = ["date", "date_day", "year_week", "year_month"].some((item) =>
      Object.keys(row).includes(item)
    );

    // ✅ Colonnes Compare et Diff si activé
    if (compareEnabled && kpi.viz !== "dimension" && !isDate) {
      // Compare
      columns.push({
        field: `${key}Compare`,
        hide: !kpi.show,
        cellClassName: sparkline ? "sparkline-cell" : undefined,
        align,
        type,
        headerName: `${kpi[locale as keyof typeof kpis[0]]} Compare`,
        minWidth,
        maxWidth,
        flex: lgUp ? 1 : undefined,
        renderHeader: () => HeaderCell("Compare", true),
        renderCell: (params: any) => cell(params.row.compare),
        valueGetter: (_, row: any) => row.compare[key],
      });

      // Diff
      columns.push({
        field: `${key}Diff`,
        hide: !kpi.show,
        align,
        type,
        headerName: `${kpi[locale as keyof typeof kpis[0]]} Difference`,
        minWidth: 225,
        width: 225,
        maxWidth: 350,
        flex: lgUp ? 1 : undefined,
        renderHeader: () => HeaderCell("Difference"),
        renderCell: (params: any) => (
          <DiffCell value={params.row[key]} compare={params.row.compare[key]} kpi={kpi} />
        ),
        valueGetter: (_, row: any) => row.diff?.[key] ?? 0,
      });
    }

    return columns;
  };
};


const useCreateColumns = <T extends GridColDef>(data: any, excludeColumns: string[] = []): T[] => {
  const { data: kpis = [] } = useKpisQuery();
  const { currentLang: { value: locale } } = useLocales();
  const lgUp = useResponsive('up', 'lg');
  const filterStore = useSelector((state: RootState) => state.filters);
  const workspace = useCurrentWorkspace();
  const makeCell = useMakeCell();

  const definedColumns: T[] = useMemo(() => {
    const make_row = (row: any, acc: GridColDef[]) => {
      Object.keys(row).forEach((key) => {
        const kpi = kpis.find((kpi) => kpi.id === key);
        const activation = filterStore?.extraOptions?.mode === "activations";
        const kpiViz = (
          kpi?.viz === "both"
          || (activation && kpi?.viz === "activation")
          || (!activation && kpi?.viz === "base")
        );

        if (
          (kpi?.viz === "dimension" || kpiViz) && row[key] !== -1) {
          const columns: GridColDef[] = makeCell(row, kpi, key);

          columns.forEach((column, i) => {
            const index = acc.findIndex((col: GridColDef) => col.field === column.field);

            if (index !== -1) {
              // Remplacer la colonne existante
              acc[index] = column;
            } else {
              acc.push(column);
            }
          });
        }
      });
      return acc;
    };

    return data.reduce((acc: GridColDef[], row: any) => make_row(row, acc), [] as GridColDef[]).sort((a: GridColDef, b: GridColDef) => {
      const aPosition = kpis.find((kpi) => kpi.id === a.field.replace("Compare", "").replace("Diff", ""))?.order ?? 0;
      const bPosition = kpis.find((kpi) => kpi.id === b.field.replace("Compare", "").replace("Diff", ""))?.order ?? 0;
      return aPosition - bPosition;
    }) as unknown as T[];
  }, [workspace, filterStore?.compare, lgUp, locale, data]);


  return definedColumns;
};

// ----------------------------------------------------------------------

type Props = {
  data: {
    id: string;
    keyword: string;
    brandSearches?: number; // Add optional brandSearches property
    [key: string]: any; // Allow for other properties that might be used
  }[];
  paginationModel: any;
  onPaginationModelChange: (model: GridPaginationModel) => void;
  paginationMode?: 'server';
  filterMode?: 'server';
  sortMode?: 'server';
  rowCount?: number;
  sortModel?: GridSortModel;
  filterModel?: GridFilterModel;
  onSortModelChange?: (model: GridSortModel) => void;
  onFilterModelChange?: (newModel: GridFilterModel) => void;
  pinnedColumns?: any[];
  onExport?: () => void;
  visibilityModel?: GridColumnVisibilityModel;
  onVisibilityModelChange?: (model: GridColumnVisibilityModel) => void;
};

export default function GenericDataGrid({
  data,
  paginationModel,
  onPaginationModelChange,
  paginationMode,
  rowCount,
  sortModel,
  onSortModelChange,
  onFilterModelChange,
  filterModel: filterModelProps,
  pinnedColumns,
  onExport,
  onVisibilityModelChange,
  visibilityModel: visibilityModelProps,
}: Props) {
  const apiRef = useGridApiRef();
  const dispatch = useDispatch();
  const theme = useTheme();
  const filterStore = useSelector((state: any) => state.filters);
  const advancedView = filterStore?.isAdvancedView;

  const handleRowClick = (params: any) => {
    const keywords = filterStore?.keywords || [];
    const clickedKeyword = params?.row?.keyword;

    let updatedKeywords;

    if (keywords.includes(clickedKeyword)) {
      updatedKeywords = keywords.filter((keyword: string) => keyword !== clickedKeyword);
    } else {
      updatedKeywords = [...keywords, clickedKeyword]
    }

    dispatch(setFilterKeyword(updatedKeywords));
  };

  // Initialize grid with filters when mounted
  useEffect(() => {
    if ((filterModelProps?.items.length ?? 0) > 0 && apiRef.current) {
      apiRef.current.setFilterModel(filterModelProps as GridFilterModel);
    }
  }, [apiRef]);

  const columns = useCreateColumns(data, pinnedColumns);

  useEffect(() => {
    apiRef.current.forceUpdate?.();
  }, [pinnedColumns, data]);

  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel | null>(null);

  const handleVisibilityChange = (newModel: GridColumnVisibilityModel) => {
    setColumnVisibilityModel(newModel);
    onVisibilityModelChange?.(newModel);
  };

  useEffect(() => {
    handleVisibilityChange({
      ...columnVisibilityModel,
      ...columns.reduce((acc, column: any) => {
        acc[column.field] = advancedView || (column.hide === true ? false : true);
        return acc;
      }, {} as any)
    })
  }, [advancedView])

  useEffect(() => {
    handleVisibilityChange(columns.reduce((acc, column: any) => {
      if (column.hide === true) {
        acc[column.field] = false;
      }
      else {
        acc[column.field] = true;
      }
      return acc;
    }, {} as any));
  }, [columns]);

  useEffect(() => {
    handleVisibilityChange({
      ...columnVisibilityModel,
      ...columns.reduce((acc, column: any) => {
        acc[column.field] = advancedView || (column.hide === true ? false : true);
        return acc;
      }, {} as any)
    })
  }, [data]);

  const Toolbar = (props: any) => {
    return (
      <Stack direction="row" alignItems="center" spacing={0}>
        <GridToolbar {...props}  />
        {onExport && (
          <Button startIcon={
            <Iconify icon="lets-icons:export" />
          } sx={{ height: 24}} color="inherit" onClick={onExport}>
            Export
          </Button>
        )}
      </Stack>
    )
  }

  return (
    <Box
      sx={{
        zIndex: -100,
        width: '100%',
        '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-cell:focus': {
          outline: 'none !important',
        },
        '& .MuiDataGrid-cell--pinnedLeft': {
          backgroundColor: theme.palette.mode === "dark" ? '#212b36 !important' : 'white!important',
        },
        '& .MuiDataGrid-cell': {
          cursor: 'pointer',
        },
      }}
    >
      <DataGridPro
        pinnedColumns={{ left: pinnedColumns }}
        autoHeight
        disableDensitySelector
        checkboxSelection={false}
        disableRowSelectionOnClick
        columnVisibilityModel={columnVisibilityModel || {}}
        onColumnVisibilityModelChange={handleVisibilityChange}
        rows={data}
        pagination
        paginationMode={paginationMode}
        sortingMode="server"
        disableMultipleColumnsSorting
        rowCount={rowCount}
        filterMode="server"
        pageSizeOptions={[10, 20, 50, 100]}
        showCellVerticalBorder={true}
        sortingOrder={['desc', 'asc', null]}
        paginationModel={paginationModel}
        onPaginationModelChange={onPaginationModelChange}
        sortModel={sortModel}
        onSortModelChange={onSortModelChange}
        filterModel={filterModelProps as GridFilterModel}
        onFilterModelChange={onFilterModelChange}
        columns={columns}
        onRowClick={handleRowClick}
        apiRef={apiRef}
        slots={{
          toolbar: Toolbar,
          filterPanel: (props) => <FilterPanelCustom {...props} getColumnForNewFilter={(args) => args ? args.columns[0].field : undefined} />,
        }}
        slotProps={{
          toolbar: { printOptions: { disableToolbarButton: true }, csvOptions: { disableToolbarButton: !!onExport } },
          filterPanel: {
            logicOperators: [GridLogicOperator.And, GridLogicOperator.Or],
          }
        }}
      />
    </Box>
  );
}
